import { useEffect, useState } from 'react';

interface Supply {
  totalSupply: number;
  circulatingSupply: number;
  burnedSupply: number;
  lockedSupply: number;
  inflationRate: number;
  activeChains: number;
  largestChainPct: number;
  timestamp: string | null;
}

interface Chain {
  chainId: number;
  chain: string;
  totalSupply: number;
  circulatingSupply: number;
  holders: number;
  transactions: number;
  pctOfTotal: number;
}

interface DistRange {
  range: string;
  holders: number;
  balance: number;
  pctSupply: number;
}

interface Concentration {
  gini: number | null;
  herfindahl: number | null;
  whaleCount: number;
  whaleShare: number;
  largeCount: number;
  largeShare: number;
  mediumCount: number;
  mediumShare: number;
  smallCount: number;
  smallShare: number;
  totalHolders: number;
}

interface SupplyPoint {
  time: string;
  totalSupply: number;
  circulatingSupply: number;
  burnedSupply: number;
  activeChains: number;
}

const CHAIN_COLORS: Record<number, string> = { 1: '#627eea', 56: '#f0b90b', 137: '#8247e5', 195: '#ff0013' };
const RANGE_LABELS: Record<string, string> = { '0-100': 'Micro (<100)', '100-1000': 'Small (100-1K)', '1000-10000': 'Medium (1K-10K)', '10000+': 'Large (10K+)' };

export function GovernanceMetrics({ refreshInterval = 120000 }: { refreshInterval?: number }) {
  const [supply, setSupply] = useState<Supply | null>(null);
  const [chains, setChains] = useState<Chain[]>([]);
  const [distribution, setDistribution] = useState<DistRange[]>([]);
  const [concentration, setConcentration] = useState<Concentration | null>(null);
  const [supplyHistory, setSupplyHistory] = useState<SupplyPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historyHours, setHistoryHours] = useState(168);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval, historyHours]);

  const loadData = async () => {
    try {
      const base = import.meta.env.VITE_API_BASE || '/api';
      const [gov, hist] = await Promise.all([
        fetch(`${base}/governance`).then(r => r.json()),
        fetch(`${base}/governance/supply-history?hours=${historyHours}`).then(r => r.json()),
      ]);
      setSupply(gov.supply || null);
      setChains(gov.chains || []);
      setDistribution(gov.distribution || []);
      setConcentration(gov.concentration || null);
      setSupplyHistory(hist.history || []);
      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load governance data');
      setLoading(false);
    }
  };

  if (loading && !supply) return <div className="governance loading">Loading governance metrics...</div>;
  if (error) return <div className="governance error">{error}</div>;

  const fmtNum = (n: number) => n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  const fmtPct = (n: number) => `${n.toFixed(1)}%`;

  // Supply donut chart
  const donutR = 60;
  const donutCx = 80;
  const donutCy = 75;
  const donutStroke = 20;
  const circ = 2 * Math.PI * donutR;

  // Chain supply segments for donut
  const chainSegments = chains.map((c, i) => {
    const offset = chains.slice(0, i).reduce((sum, ch) => sum + ch.pctOfTotal, 0);
    return { ...c, offset };
  });

  // Supply history chart
  const chartW = 580;
  const chartH = 120;
  const pad = { top: 8, right: 8, bottom: 18, left: 45 };
  const plotW = chartW - pad.left - pad.right;
  const plotH = chartH - pad.top - pad.bottom;

  const supplyVals = supplyHistory.map(h => h.totalSupply);
  const minSupply = supplyVals.length > 0 ? Math.min(...supplyVals) * 0.999 : 0;
  const maxSupply = supplyVals.length > 0 ? Math.max(...supplyVals) * 1.001 : 1;
  const supplyRange = maxSupply - minSupply || 1;

  const supplyPath = supplyHistory.length > 1
    ? supplyHistory.map((h, i) => {
        const x = pad.left + (i / (supplyHistory.length - 1)) * plotW;
        const y = pad.top + plotH - ((h.totalSupply - minSupply) / supplyRange) * plotH;
        return `${i === 0 ? 'M' : 'L'}${x},${y}`;
      }).join(' ')
    : '';

  // Distribution bar max
  const maxPct = Math.max(...distribution.map(d => d.pctSupply), 1);

  return (
    <div className="governance">
      {/* Supply Overview Cards */}
      {supply && (
        <div className="gov-summary">
          <div className="gov-cards">
            <div className="gov-card">
              <span className="gov-label">Total Supply</span>
              <span className="gov-value">{fmtNum(supply.totalSupply)}</span>
              <span className="gov-sub">{supply.activeChains} chains</span>
            </div>
            <div className="gov-card">
              <span className="gov-label">Circulating</span>
              <span className="gov-value">{fmtNum(supply.circulatingSupply)}</span>
              <span className="gov-sub">100% of total</span>
            </div>
            <div className="gov-card">
              <span className="gov-label">Total Holders</span>
              <span className="gov-value">{concentration ? fmtNum(concentration.totalHolders) : 'N/A'}</span>
              <span className="gov-sub">Across all chains</span>
            </div>
            <div className="gov-card">
              <span className="gov-label">Gini Index</span>
              <span className="gov-value">{concentration?.gini?.toFixed(3) ?? 'N/A'}</span>
              <span className="gov-sub">{concentration?.gini && concentration.gini > 0.6 ? 'Concentrated' : 'Moderate'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Chain Distribution Donut + Table */}
      <div className="gov-chain-section">
        <div className="gov-donut-area">
          <h4>Supply by Chain</h4>
          <svg viewBox={`0 0 ${donutCx * 2} ${donutCy * 2}`} className="donut-chart">
            {chainSegments.map((c) => {
              const dashLen = (c.pctOfTotal / 100) * circ;
              const dashOffset = -(c.offset / 100) * circ;
              return (
                <circle
                  key={c.chainId}
                  cx={donutCx} cy={donutCy} r={donutR}
                  fill="none"
                  stroke={CHAIN_COLORS[c.chainId] || '#888'}
                  strokeWidth={donutStroke}
                  strokeDasharray={`${dashLen} ${circ - dashLen}`}
                  strokeDashoffset={dashOffset}
                  transform={`rotate(-90, ${donutCx}, ${donutCy})`}
                />
              );
            })}
            <text x={donutCx} y={donutCy - 6} textAnchor="middle" className="donut-center-text">
              {fmtNum(supply?.totalSupply || 0)}
            </text>
            <text x={donutCx} y={donutCy + 12} textAnchor="middle" className="donut-sub-text">
              FCO Total
            </text>
          </svg>
        </div>

        <div className="gov-chain-table">
          <div className="chain-table-header">
            <span>Chain</span><span>Supply</span><span>% Total</span><span>Holders</span><span>Txns</span>
          </div>
          {chains.map((c) => (
            <div key={c.chainId} className="chain-table-row">
              <span className="chain-dot" style={{ color: CHAIN_COLORS[c.chainId] || '#888' }}>
                {c.chain}
              </span>
              <span>{fmtNum(c.totalSupply)}</span>
              <span>{fmtPct(c.pctOfTotal)}</span>
              <span>{c.holders}</span>
              <span>{fmtNum(c.transactions)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Holder Distribution Bars */}
      <div className="gov-distribution">
        <h4>Holder Distribution</h4>
        <div className="dist-bars">
          {distribution.map((d) => (
            <div key={d.range} className="dist-row">
              <span className="dist-label">{RANGE_LABELS[d.range] || d.range}</span>
              <div className="dist-bar-bg">
                <div className="dist-bar-fill" style={{ width: `${(d.pctSupply / maxPct) * 100}%` }}>
                  {d.pctSupply > 5 && <span className="dist-bar-text">{fmtPct(d.pctSupply)}</span>}
                </div>
              </div>
              <div className="dist-stats">
                <span className="dist-holders">{d.holders} holders</span>
                <span className="dist-balance">{fmtNum(d.balance)} FCO</span>
                <span className="dist-pct">{fmtPct(d.pctSupply)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Supply History Chart */}
      {supplyHistory.length > 1 && (
        <div className="gov-supply-history">
          <div className="history-header">
            <h4>Total Supply History</h4>
            <div className="history-range">
              {[24, 168, 720].map((h) => (
                <button key={h} className={`range-btn ${historyHours === h ? 'active' : ''}`}
                  onClick={() => setHistoryHours(h)}>
                  {h === 24 ? '24h' : h === 168 ? '7d' : '30d'}
                </button>
              ))}
            </div>
          </div>
          <svg viewBox={`0 0 ${chartW} ${chartH}`} className="supply-chart">
            <text x={pad.left - 4} y={pad.top + 8} textAnchor="end" className="axis-label">{fmtNum(maxSupply)}</text>
            <text x={pad.left - 4} y={pad.top + plotH + 4} textAnchor="end" className="axis-label">{fmtNum(minSupply)}</text>
            <line x1={pad.left} y1={pad.top + plotH / 2} x2={pad.left + plotW} y2={pad.top + plotH / 2} className="grid-line" />
            <path d={supplyPath} fill="none" stroke="#4ecdc4" strokeWidth="2" />
            {supplyHistory.length > 0 && (
              <>
                <text x={pad.left} y={chartH - 2} className="axis-label">
                  {new Date(supplyHistory[0].time).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </text>
                <text x={pad.left + plotW} y={chartH - 2} textAnchor="end" className="axis-label">
                  {new Date(supplyHistory[supplyHistory.length - 1].time).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </text>
              </>
            )}
          </svg>
        </div>
      )}

      {/* Concentration Summary */}
      {concentration && (
        <div className="gov-concentration">
          <h4>Token Concentration</h4>
          <div className="conc-grid">
            {[
              { label: 'Whales', count: concentration.whaleCount, share: concentration.whaleShare, color: '#ff4d4d' },
              { label: 'Large Holders', count: concentration.largeCount, share: concentration.largeShare, color: '#ff8c42' },
              { label: 'Medium Holders', count: concentration.mediumCount, share: concentration.mediumShare, color: '#4ecdc4' },
              { label: 'Small Holders', count: concentration.smallCount, share: concentration.smallShare, color: '#45b7d1' },
            ].map((cat) => (
              <div key={cat.label} className="conc-card" style={{ borderTopColor: cat.color }}>
                <span className="conc-label">{cat.label}</span>
                <span className="conc-count">{cat.count}</span>
                <span className="conc-share">{fmtPct(cat.share)} of supply</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {supply?.timestamp && (
        <div className="gov-updated">Last updated: {new Date(supply.timestamp).toLocaleString()}</div>
      )}
    </div>
  );
}
